import { Component } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  of,
} from 'rxjs';
import { CountryControllerGetAll$Params } from '../../../api/fn/country/country-controller-get-all';
import { IdentityControllerGetAll$Params } from '../../../api/fn/identity/identity-controller-get-all';
import {
  UserItem,
  CountryItem,
  TravelItemMinimal,
  UserItemMinimal,
  TravelCategory,
} from '../../../api/models';
import {
  TravelService,
  IdentityService,
  CountryService,
} from '../../../api/services';
import { enumUserRole } from '../../../shared/utils/enum-user-role';
import { ActivatedRoute, Router } from '@angular/router';
import { correctDate } from '../../../shared/utils/utils';
import { IdentityControllerGetAllTourLeaders$Params } from '../../../api/fn/identity/identity-controller-get-all-tour-leaders';
import { unwrapDeep } from '../../../shared/utils/utils-from-git';

@Component({
  selector: 'app-search-travels',
  templateUrl: './search-travels.component.html',
  styleUrl: './search-travels.component.scss',
})
export class SearchTravelsComponent {
  autocompleteLeaders$ = new BehaviorSubject<string>('');
  suggestedLeaders: UserItem[] = [];
  autocompleteCountries$ = new BehaviorSubject<string>('');
  suggestedCountries: CountryItem[] = [];
  travels: TravelItemMinimal[] = [];
  limit = 10;
  skip = 0;
  totalRecords = 0;
  today = new Date();
  price_opts = [
    { value: '>1500' },
    { value: '1500-2000' },
    { value: '2000-2500' },
    { value: '<2500' },
  ];
  orderBy = [
    { name: 'Prezzo', value: 'price_room' },
    { name: 'Date', value: 'from_date' },
    { name: 'Dimensione del gruppo', value: 'max_members' },
    { name: 'Nessun ordine', value: undefined },
  ];
  types: TravelCategory[] = [];
  loading = false;

  frmSearch: FormGroup = new FormGroup({
    destinations: new FormControl(undefined),
    categories: new FormControl<number[] | undefined>(undefined),
    from: new FormControl(undefined),
    to: new FormControl(undefined),
    leader: new FormControl<number | undefined>(undefined),
    price: new FormControl<string | undefined>(undefined),
    flight: new FormControl<boolean>(false),
  });

  orderBy_control = new FormControl('', { nonNullable: true });

  constructor(
    private title: Title,
    private travelService: TravelService,
    private identityService: IdentityService,
    private countryService: CountryService,
    private ar: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    this.title.setTitle('SocialTravels - Tutti i viaggi');
    this.getTravelTypes();

    this.autocompleteLeaders$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          if (query) {
            let params: IdentityControllerGetAllTourLeaders$Params = {
              limit: 1000,
              skip: 0,
              query,
            };
            return this.identityService.identityControllerGetAllTourLeaders(
              params,
            );
          } else {
            return of({
              data: { list: [] },
            });
          }
        }),
      )
      .subscribe({
        next: (res) => {
          this.suggestedLeaders = res.data.list;
          if (this.suggestedLeaders.length === 1) {
            this.frmSearch.get('leader')?.setValue(this.suggestedLeaders[0]);
          }
        },
      });

    this.autocompleteCountries$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          if (query) {
            let params: CountryControllerGetAll$Params = {
              query,
            };
            return this.countryService.countryControllerGetAll(params);
          } else {
            return of({
              data: { list: [] },
            });
          }
        }),
      )
      .subscribe({
        next: (res) => {
          this.suggestedCountries = res.data.list;
          if (this.suggestedCountries.length === 1) {
            this.frmSearch
              .get('destinations')
              ?.setValue(this.suggestedCountries[0]);
          }
        },
      });

    this.getParams();
  }

  getParams() {
    this.ar.queryParams.subscribe((v) => {
      if (v) {
        if (v['countries']) {
          this.frmSearch.get('destinations')?.setValue(v['countries']);
        }
        if (v['categories']) {
          if (Array.isArray(v['categories'])) {
            this.frmSearch
              .get('categories')
              ?.setValue((v['categories'] as string[]).map((x) => +x));
          } else {
            this.frmSearch.get('categories')?.setValue([+v['categories']]);
          }
        }
        if (v['from_date']) {
          this.frmSearch.get('from')?.setValue(new Date(v['from_date']));
        }
        if (v['to_date']) {
          this.frmSearch.get('to')?.setValue(new Date(v['to_date']));
        }
        if (v['leaders']) {
          this.frmSearch.get('leader')?.setValue(v['leaders']);
        }
        if (v['price_from'] || v['price_to']) {
          if (v['price_from'] === '' && v['price_to'] === '>1500') {
            this.frmSearch.get('price')?.setValue('>1500');
          } else if (v['price_from'] === '1500') {
            this.frmSearch.get('price')?.setValue('1500-2000');
          } else if (v['price_from'] === '2000') {
            this.frmSearch.get('price')?.setValue('2000-2500');
          } else if (v['price_to'] === '2500') {
            this.frmSearch.get('price')?.setValue('<2500');
          }
        }
        if (v['sort_by']) {
          this.orderBy_control.setValue(v['sort_by']);
        }
      }
      this.getTravels();
    });
  }

  filterLeaders(event: AutoCompleteCompleteEvent) {
    let query = event.query;
    this.autocompleteLeaders$.next(query);
  }

  filterCountries(event: AutoCompleteCompleteEvent) {
    let query = event.query;
    this.autocompleteCountries$.next(query);
  }

  getLeadersNameToDisplay(u: UserItemMinimal) {
    return u.name + ' ' + u.last_name;
  }

  showMore() {
    this.skip += this.limit;
    this.submitSearch();
  }

  getTravelTypes() {
    this.travelService.travelControllerGetCategories().subscribe({
      next: (res) => {
        this.types = res.data.list;
      },
    });
  }

  // questo metodo intercetta la submit, formula la qs e fa il redirect
  submitSearch() {
    const country_obj = this.frmSearch.get('destinations')?.value;
    const cats = this.frmSearch.get('categories')?.value;
    const from = this.frmSearch.get('from')?.value
      ? correctDate(this.frmSearch.get('from')?.value).toISOString()
      : undefined;
    const to = this.frmSearch.get('to')?.value
      ? correctDate(this.frmSearch.get('to')?.value).toISOString()
      : undefined;
    const leader = this.frmSearch.get('leader')?.value
      ? this.frmSearch.get('leader')?.value
      : undefined;

    let price_from = undefined;
    let price_to = undefined;
    if (this.frmSearch.get('price')?.value === '>1500') {
      price_from = undefined;
      price_to = 1500;
    } else if (this.frmSearch.get('price')?.value === '1500-2000') {
      price_from = 1500;
      price_to = 2000;
    } else if (this.frmSearch.get('price')?.value === '2000-2500') {
      price_from = 2000;
      price_to = 2500;
    } else if (this.frmSearch.get('price')?.value === '<2500') {
      price_from = 2500;
      price_to = undefined;
    } else {
      price_from = undefined;
      price_to = undefined;
    }
    const flight = this.frmSearch.get('flight')?.value;
    const sortBy = this.orderBy_control.value;

    if (
      country_obj ||
      cats ||
      from ||
      to ||
      leader ||
      price_from ||
      price_to ||
      flight
    ) {
      this.router.navigate(['/viaggi-di-gruppo/search'], {
        queryParams: {
          countries: country_obj?.ISO,
          categories: cats,
          from_date: from,
          to_date: to,
          tour_leaders: leader?.id,
          price_from: price_from,
          price_to: price_to,
          with_flights: flight,
          limit: this.limit,
          skip: this.skip,
          sort_by: sortBy,
        },
      });
    } else {
      this.router.navigate(['/viaggi-di-gruppo/search'], { queryParams: {} });
    }
  }
  resetFilters() {
    this.frmSearch.reset({
      destinations: null,
      categories: null,
      from: null,
      to: null,
      leader: null,
      price: null,
      flight: false,
    });

    // reset anche dei controlli esterni al form
    this.orderBy_control.setValue('from_date');
    this.skip = 0;

    this.router.navigate(['/viaggi-di-gruppo/search'], {
      queryParams: {},
    });
  }

  getTravels() {
    const country_obj = this.frmSearch.get('destinations')?.value;
    const cats = this.frmSearch.get('categories')?.value;
    const from = this.frmSearch.get('from')?.value
      ? correctDate(this.frmSearch.get('from')?.value).toISOString()
      : undefined;
    const to = this.frmSearch.get('to')?.value
      ? correctDate(this.frmSearch.get('to')?.value).toISOString()
      : undefined;
    const leader = this.frmSearch.get('leader')?.value;

    let price_from = undefined;
    let price_to = undefined;
    if (this.frmSearch.get('price')?.value === '>1500') {
      price_from = undefined;
      price_to = 1500;
    } else if (this.frmSearch.get('price')?.value === '1500-2000') {
      price_from = 1500;
      price_to = 2000;
    } else if (this.frmSearch.get('price')?.value === '2000-2500') {
      price_from = 2000;
      price_to = 2500;
    } else if (this.frmSearch.get('price')?.value === '<2500') {
      price_from = 2500;
      price_to = undefined;
    } else {
      price_from = undefined;
      price_to = undefined;
    }
    const flight = this.frmSearch.get('flight')?.value;
    const sortBy = this.orderBy_control.value;

    this.loading = true;

    this.travelService
      .travelControllerGetAll({
        limit: this.limit,
        skip: this.skip,
        categories: cats,
        countries: country_obj,
        from_date: from,
        to_date: to,
        price_from: price_from,
        price_to: price_to,
        sort_by: sortBy,
        tour_leaders: leader?.id,
        with_flights: flight,
      })
      .subscribe({
        next: (res) => {
          this.travels = res.data.list;
          this.totalRecords = res.data.count;
          if (res.data.countries && res.data.countries.length > 0) {
            this.frmSearch.get('destinations')?.setValue(res.data.countries[0]);
          }
          if (res.data.tour_leaders && res.data.tour_leaders.length > 0) {
            this.frmSearch.get('leaders')?.setValue(res.data.tour_leaders[0]);
          }
          this.travels.sort((a, b) => {
            return (
              new Date(a.from_date).getTime() - new Date(b.from_date).getTime()
            );
          });
          this.loading = false;
        },
      });
  }
}
